import { GraphQL } from "@rpi/openapi-api";
import { UIMatch, useMatches } from "react-router-dom";

export const DASHBOARD_PATH = "/dashboard";
export const ADMIN_DASHBOARD_PATH = `${DASHBOARD_PATH}/admin`;

export interface RouteHandle {
  pageId: string;
  requiredRole?: string[];
  navigateOnSandboxToggle?: string;
}

export const isRouteHandle = (handle: unknown): handle is RouteHandle =>
  typeof handle === "object" && handle !== null && typeof (handle as { pageId?: unknown }).pageId === "string";

export function useTypedMatches() {
  const untypedMatches = useMatches();
  return untypedMatches as UIMatch<unknown, RouteHandle>[];
}

export function withHandle(
  pageId: string,
  options?: { requiredRole?: string | string[]; navigateOnSandboxToggle?: string }
) {
  const handle: RouteHandle = {
    pageId,
    navigateOnSandboxToggle: options?.navigateOnSandboxToggle,
  };
  if (options?.requiredRole) {
    handle.requiredRole = Array.isArray(options.requiredRole) ? options.requiredRole : [options.requiredRole];
  }
  return { handle };
}

// prettier-ignore
export const routePaths = {
  "auth:sign-in": "/auth/sign-in",
  "auth:sign-up": "/auth/sign-up",
  "auth:reset-password": "/auth/reset-password",

  "onboarding": "/onboarding",
  "setup-payment": "/onboarding/setup-payment",

  "home": DASHBOARD_PATH,

  "settings": `${DASHBOARD_PATH}/settings`,
  "settings:edit-account": `${DASHBOARD_PATH}/settings/edit-account`,
  "settings:edit-password": `${DASHBOARD_PATH}/settings/edit-password`,
  "settings:edit-org": `${DASHBOARD_PATH}/settings/edit-org`,
  "settings:tax-exempt-upload": `${DASHBOARD_PATH}/settings/tax-exempt-upload`,
  "settings:add-payment-method": `${DASHBOARD_PATH}/settings/payment-method/add`,
  "settings:edit-payment-method": ({ token }: { token: NonNullable<GraphQL.BraintreePaymentMethodResponse["token"]> }) => `${DASHBOARD_PATH}/settings/payment-method/${encodeURIComponent(token)}/edit`,

  "customer-products": `${DASHBOARD_PATH}/customer-products`,
  "customer-product": ({ id }: Pick<GraphQL.CustomerProductResponse, "id">) => `${DASHBOARD_PATH}/customer-products/${id}`,
  "customer-product:edit": ({ id }: Pick<GraphQL.CustomerProductResponse, "id">) => `${DASHBOARD_PATH}/customer-products/${id}/edit`,
  "customer-product:create": `${DASHBOARD_PATH}/customer-products/create`,


  "print-assets": `${DASHBOARD_PATH}/print-assets`,
  "print-asset": ({ id }: Pick<GraphQL.PrintAssetResponse, "id">) => `${DASHBOARD_PATH}/print-assets/${id}`,

  "orders": `${DASHBOARD_PATH}/orders`,
  "order": ({ customerPublicId, customerOrderId }: { customerPublicId: string; customerOrderId: string }) => `${DASHBOARD_PATH}/orders/${encodeURIComponent(customerPublicId)}/${encodeURIComponent(customerOrderId)}`,
  "order:details": (params: { customerPublicId: string; customerOrderId: string }) => `${routePaths.order(params)}/details`,
  "order:items": (params: { customerPublicId: string; customerOrderId: string }) => `${routePaths.order(params)}/items`,
  "order:event-logs": (params: { customerPublicId: string; customerOrderId: string }) => `${routePaths.order(params)}/event-logs`,
  "order:shipments": (params: { customerPublicId: string; customerOrderId: string }) => `${routePaths.order(params)}/shipments`,

  "api-credentials": `${DASHBOARD_PATH}/api-credentials`,
  "api-credential:create": `${DASHBOARD_PATH}/api-credentials/create`,
  "api-examples": `${DASHBOARD_PATH}/api-examples`,

  "customers": `${ADMIN_DASHBOARD_PATH}/customers`,
  "customer": ({ publicId }: Pick<GraphQL.CustomerResponse, "publicId">) => `${ADMIN_DASHBOARD_PATH}/customers/${encodeURIComponent(publicId)}`,
  "customer:edit": ({ publicId }: Pick<GraphQL.CustomerResponse, "publicId">) => `${ADMIN_DASHBOARD_PATH}/customers/${encodeURIComponent(publicId)}/edit`,
  "customer:edit-type": ({ publicId }: Pick<GraphQL.CustomerResponse, "publicId">) => `${ADMIN_DASHBOARD_PATH}/customers/${encodeURIComponent(publicId)}/edit-type`,
  "customer:create": `${ADMIN_DASHBOARD_PATH}/customers/create`,

  "open-api-products": `${ADMIN_DASHBOARD_PATH}/open-api-products`,
  "open-api-product": ({ id }: Pick<GraphQL.OpenapiProductResponse, "id">) => `${ADMIN_DASHBOARD_PATH}/open-api-products/${id}`,

  "dashboard-users": `${ADMIN_DASHBOARD_PATH}/dashboard-users`,
  "dashboard-user": ({ id }: Pick<GraphQL.DashboardUserResponse, "id">) => `${ADMIN_DASHBOARD_PATH}/dashboard-users/${id}`,
  "dashboard-user:edit": ({ id }: Pick<GraphQL.DashboardUserResponse, "id">) => `${ADMIN_DASHBOARD_PATH}/dashboard-users/${id}/edit`,
  "dashboard-user:create": `${ADMIN_DASHBOARD_PATH}/dashboard-users/create`,

  "billing": `${ADMIN_DASHBOARD_PATH}/billing`,
  "payment-method:edit": ({ token }: { token: NonNullable<GraphQL.BraintreePaymentMethodResponse["token"]> }) => `${ADMIN_DASHBOARD_PATH}/billing/payment-methods/${encodeURIComponent(token)}/edit`,
  "billing:file-upload": `${ADMIN_DASHBOARD_PATH}/billing/file-upload`,

  "uploads": `${ADMIN_DASHBOARD_PATH}/uploads`,
  "uploads:upload": `${ADMIN_DASHBOARD_PATH}/uploads/upload`,
  "uploads:collection": ({ collectionId, customerPublicId }: Pick<GraphQL.UploadedFileResponse, "collectionId"> & { customerPublicId: GraphQL.CustomerResponse["publicId"]}) => (
    `${ADMIN_DASHBOARD_PATH}/uploads/customers/${encodeURIComponent(customerPublicId)}/collections/${encodeURIComponent(collectionId)}`
  ),

  "payment": ({ id }: Pick<GraphQL.PaymentResponse, "id">) => `${ADMIN_DASHBOARD_PATH}/payments/${id}`,

  "configuration": `${ADMIN_DASHBOARD_PATH}/configuration`,
} as const;
